import { useEffect, useState } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Toaster } from 'react-hot-toast';
import { AuthGate, useSession } from '../auth/session.tsx';
import { AssistantPanel } from './AssistantPanel.tsx';
import { Drawer } from './Drawer.tsx';
import { AssistantProvider, ASSISTANT_OPEN_EVENT } from '../assistant/AssistantContext.tsx';
import { AssistantLaunchButton } from '../assistant/AssistantLaunchButton.tsx';
import { LivePresenceProvider } from '../live/LivePresenceContext.tsx';
import { BroadcastLauncher } from './broadcast/BroadcastLauncher.tsx';
import { BroadcastBanner } from './broadcast/BroadcastBanner.tsx';
import {
  DashboardIcon,
  ScreensIcon,
  MediaIcon,
  PlaylistsIcon,
  SchedulesIcon,
  AnalyticsIcon,
  SettingsIcon,
  AdminIcon,
} from './NavIcons.tsx';

/** Dark-theme styling for react-hot-toast (its default surface is light). Shared
    with the auth pages so toasts match the app everywhere. */
export const DARK_TOAST = {
  duration: 4000,
  style: {
    background: 'var(--surface-2)',
    color: 'var(--text)',
    border: '1px solid var(--border-strong)',
    borderRadius: '10px',
    fontSize: '13px',
    boxShadow: 'var(--shadow-lg)',
  },
  success: { iconTheme: { primary: 'var(--ok)', secondary: 'var(--bg)' } },
  error: { iconTheme: { primary: 'var(--danger)', secondary: 'var(--bg)' } },
} as const;

const NAV = [
  { to: '/', label: 'Dashboard', Icon: DashboardIcon },
  { to: '/screens', label: 'Screens', Icon: ScreensIcon },
  { to: '/media', label: 'Media', Icon: MediaIcon },
  { to: '/playlists', label: 'Playlists', Icon: PlaylistsIcon },
  { to: '/schedules', label: 'Schedules', Icon: SchedulesIcon },
  { to: '/analytics', label: 'Analytics', Icon: AnalyticsIcon },
  { to: '/settings', label: 'Settings', Icon: SettingsIcon },
] as const;

function Sidebar() {
  const { permissions } = useSession();
  return (
    <nav className="sidebar" aria-label="Primary">
      <div className="brand">
        Seen<span>Cast</span>
        <div className="brand-sub">by SeenLabs</div>
      </div>
      {NAV.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="nav-link"
          activeProps={{ className: 'nav-link active' }}
          activeOptions={{ exact: item.to === '/' }}
        >
          <span className="ico" aria-hidden>
            <item.Icon />
          </span>
          {item.label}
        </Link>
      ))}
      {permissions.isSuperadmin && (
        <>
          <div className="nav-section">SeenLabs</div>
          <Link
            to="/admin"
            className="nav-link"
            activeProps={{ className: 'nav-link active' }}
          >
            <span className="ico" aria-hidden>
              <AdminIcon />
            </span>
            Admin
          </Link>
        </>
      )}
      <div className="nav-spacer" />
      <div className="nav-footer">v0.1.0 · Phase 1</div>
    </nav>
  );
}

function OrgSwitcher() {
  const { me, activeOrgId, switchOrg } = useSession();
  if (me.orgs.length <= 1) {
    return <span className="text-dim">{me.orgs.find((o) => o.id === activeOrgId)?.name}</span>;
  }
  return (
    <div className="org-switcher">
      <label className="sr-only" htmlFor="org-switch">
        Organization
      </label>
      <select
        id="org-switch"
        aria-label="Switch organization"
        value={activeOrgId}
        onChange={(e) => void switchOrg(e.target.value)}
      >
        {me.orgs.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function Topbar({
  onToggleAssistant,
  onOpenNav,
}: {
  onToggleAssistant: () => void;
  onOpenNav: () => void;
}) {
  const { me, role, logout } = useSession();
  return (
    <header className="topbar">
      {/* Hamburger + wordmark are mobile-only (hidden ≥840px in app.css); they
          replace the sidebar, which collapses into the nav drawer on phones. */}
      <button
        className="btn ghost icon topbar-hamburger"
        aria-label="Open navigation menu"
        onClick={onOpenNav}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
          <path d="M3 6h18M3 12h18M3 18h18" />
        </svg>
      </button>
      <span className="topbar-brand" aria-hidden>
        Seen<span>Cast</span>
      </span>
      <OrgSwitcher />
      <span className="chip topbar-role">{role}</span>
      {/* Org-wide broadcast — placed by the org switcher so it reads as an
          org-level action, not a per-page one. Hidden for viewers inside. */}
      <BroadcastLauncher />
      <div className="spacer" />
      <span className="text-dim topbar-user">{me.user.name}</span>
      {/* Assistant launcher — the product's differentiator (audit P1-1). Desktop
          accent lives in s6-assistant's <AssistantLaunchButton> (filled teal +
          sparkle). It MUST stay reachable on mobile: its `.asst-launch` label
          collapses to an icon-only control below 840px (see app.css §mobile) but
          the button never disappears. Placement/reachability are owned here. */}
      <AssistantLaunchButton onClick={onToggleAssistant} />
      <button className="btn ghost sm" onClick={() => void logout()}>
        Sign out
      </button>
    </header>
  );
}

function Shell() {
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Close the mobile nav sheet on every route change (nav closes on navigation).
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  // Keyboard shortcut to toggle the assistant (docs/06 §assistant panel), plus
  // the "Ask assistant" deep-link signal (s6-assistant): AssistantContext.ask()
  // queues a prompt and fires ASSISTANT_OPEN_EVENT so the panel opens to drain it.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setAssistantOpen((v) => !v);
      }
    }
    function onAssistantOpen() {
      setAssistantOpen(true);
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener(ASSISTANT_OPEN_EVENT, onAssistantOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(ASSISTANT_OPEN_EVENT, onAssistantOpen);
    };
  }, []);

  return (
    // AssistantProvider sits above the panel so the conversation survives the
    // panel closing and route changes (the Shell stays mounted across nav).
    // LivePresenceProvider opens the single session-wide presence socket here, so
    // every screen surface below reads live online/now-playing from one source.
    <AssistantProvider>
     <LivePresenceProvider>
      <div className={`app${assistantOpen ? ' assistant-open' : ''}`}>
        {/* Desktop sidebar — hidden below 840px, where it moves into the drawer. */}
        <Sidebar />
        <div className="content">
          <Topbar
            onToggleAssistant={() => setAssistantOpen((v) => !v)}
            onOpenNav={() => setNavOpen(true)}
          />
          {/* Org-wide broadcast status — spans the Studio, above every page. */}
          <BroadcastBanner />
          <Outlet />
        </div>
        {/* Mobile nav sheet: the same sidebar inside a left-docked drawer. */}
        {navOpen && (
          <Drawer title="Menu" side="left" className="nav-drawer" onClose={() => setNavOpen(false)}>
            <Sidebar />
          </Drawer>
        )}
        {assistantOpen && <AssistantPanel onClose={() => setAssistantOpen(false)} />}
        <Toaster position="bottom-right" toastOptions={DARK_TOAST} />
      </div>
     </LivePresenceProvider>
    </AssistantProvider>
  );
}

/** Authenticated application shell used by all in-app routes. */
export function AppLayout() {
  return (
    <AuthGate>
      <Shell />
    </AuthGate>
  );
}
